import {FC, useEffect, useState} from 'react'
import {Link} from 'react-router-dom'
import {api, Booking, ApiError} from '../../services/api'
import {BookingTimelineModal, BookingStatusPill, PaidPill, stayEndDate} from '../../components/BookingTimelineModal'
import {useIsMobile} from '../../hooks/useIsMobile'
import {Spinner} from '../../components/Spinner'
import {PageHead} from './ui'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'

const EyeIcon: FC = () => (
  <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z' />
    <circle cx='12' cy='12' r='3' />
  </svg>
)

// Newest booking first (most recently created on top).
const byPriority = (a: Booking, b: Booking): number =>
  +new Date(b.createdAt) - +new Date(a.createdAt)

/** Paid = full total (fixed-term) or any rent recorded / active sub (monthly). */
const isPaid = (b: Booking): boolean =>
  b.amount != null
    ? b.paid
    : Number(b.paidAmount ?? 0) > 0 || b.subscriptionStatus === 'active' || b.hasSubscription

const BookingDetailsPage: FC = () => {
  const [bookings, setBookings] = useState<Booking[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<Booking | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const isMobile = useIsMobile()
  const sorted = bookings ? [...bookings].sort(byPriority) : []

  // Refetch the latest data (status + payments) before opening the timeline.
  const openTimeline = async (b: Booking) => {
    setBusyId(b.id)
    try {
      const all = await api.myBookings()
      setBookings(all)
      setSelected(all.find((x) => x.id === b.id) ?? b)
    } catch {
      setSelected(b) // fall back to the row data if the refetch fails
    } finally {
      setBusyId(null)
    }
  }

  useEffect(() => {
    api
      .myBookings()
      .then(setBookings)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load bookings'))
  }, [])

  return (
    <div>
      <PageHead icon='📅' eyebrow='Your stays' title='My bookings' sub='Tap a booking to see its full timeline.' />

      {error ? (
        <div style={{padding: 40, textAlign: 'center', color: '#d9214e'}}>{error}</div>
      ) : bookings === null ? (
        <div style={{padding: 40, textAlign: 'center', color: MUTED}}>Loading…</div>
      ) : bookings.length === 0 ? (
        <div style={{background: '#fff', borderRadius: 16, padding: 40, textAlign: 'center', color: MUTED, boxShadow: '0 6px 18px rgba(27,27,41,0.06)'}}>
          No bookings yet. <Link to='/rooms' style={{color: ACCENT, fontWeight: 700, textDecoration: 'none'}}>Browse rooms →</Link>
        </div>
      ) : (
        <div className='ccp-fade-up-2' style={{background: '#fff', borderRadius: 16, boxShadow: '0 6px 18px rgba(27,27,41,0.06)', overflow: 'hidden'}}>
          {isMobile ? (
            <div>
              {sorted.map((b) => (
                <div key={b.id} onClick={() => openTimeline(b)} style={{borderTop: '1px solid #f1f3f7', padding: '14px 16px', cursor: 'pointer', opacity: busyId === b.id ? 0.6 : 1}}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10}}>
                    <div style={{fontSize: 18, fontWeight: 800, color: INK}}>{b.bed?.code ?? '—'}</div>
                    <BookingStatusPill status={b.status} />
                  </div>
                  <div style={{fontSize: 13, color: MUTED, marginTop: 6}}>{b.startDate} → {stayEndDate(b) ?? 'ongoing'}{b.months ? ` · ${b.months}mo` : ' · monthly'}</div>
                  <div style={{display: 'flex', alignItems: 'center', gap: 12, marginTop: 8}}>
                    <span style={{fontSize: 16, fontWeight: 800, color: INK}}>{b.amount ? `$${b.amount}` : b.unitPrice ? `$${Number(b.unitPrice).toFixed(0)}/mo` : '—'}</span>
                    <PaidPill paid={isPaid(b)} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
              <thead>
                <tr style={{textAlign: 'left', color: MUTED, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em'}}>
                  <th style={th}>Bed</th>
                  <th style={th}>Stay</th>
                  <th style={th}>Total</th>
                  <th style={th}>Payment</th>
                  <th style={th}>Status</th>
                  <th style={{...th, textAlign: 'right'}}>View</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((b) => (
                  <tr key={b.id} onClick={() => openTimeline(b)} className='ccp-row' style={{borderTop: '1px solid #f1f3f7', cursor: 'pointer'}}>
                    <td style={{...td, fontWeight: 700, color: INK}}>{b.bed?.code ?? '—'}</td>
                    <td style={{...td, color: MUTED, whiteSpace: 'nowrap'}}>{b.startDate} → {stayEndDate(b) ?? 'ongoing'}{b.months ? ` · ${b.months}mo` : ' · monthly'}</td>
                    <td style={{...td, fontWeight: 700, color: INK}}>{b.amount ? `$${b.amount}` : b.unitPrice ? `$${Number(b.unitPrice).toFixed(0)}/mo` : '—'}</td>
                    <td style={td}><PaidPill paid={isPaid(b)} /></td>
                    <td style={td}><BookingStatusPill status={b.status} /></td>
                    <td style={{...td, textAlign: 'right'}}>
                      <button
                        onClick={(e) => {e.stopPropagation(); openTimeline(b)}}
                        disabled={busyId === b.id}
                        title='View timeline'
                        aria-label='View timeline'
                        style={{display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: ACCENT, cursor: busyId === b.id ? 'wait' : 'pointer'}}
                      >
                        {busyId === b.id ? <Spinner size={13} /> : <EyeIcon />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}

      {selected && <BookingTimelineModal booking={selected} onClose={() => setSelected(null)} />}

      <style>{`.ccp-row:hover { background: #f8fafc; }`}</style>
    </div>
  )
}

const th: React.CSSProperties = {padding: '13px 18px', fontWeight: 600}
const td: React.CSSProperties = {padding: '14px 18px', verticalAlign: 'middle'}

export {BookingDetailsPage}
